import type { CategoryTone } from '@zealot/ui/lib/categoryTones';
import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

const CATEGORY_TONE_CLASSES = {
  'category-rose': 'bg-category-rose/15 text-category-rose',
  'category-orange': 'bg-category-orange/15 text-category-orange',
  'category-amber': 'bg-category-amber/15 text-category-amber',
  'category-lime': 'bg-category-lime/15 text-category-lime',
  'category-green': 'bg-category-green/15 text-category-green',
  'category-teal': 'bg-category-teal/15 text-category-teal',
  'category-blue': 'bg-category-blue/15 text-category-blue',
  'category-indigo': 'bg-category-indigo/15 text-category-indigo',
  'category-purple': 'bg-category-purple/15 text-category-purple',
  'category-pink': 'bg-category-pink/15 text-category-pink',
} satisfies Record<CategoryTone, string>;

// gloss
const iconChipVariants = cva('inline-flex shrink-0 items-center justify-center', {
  variants: {
    tone: {
      default: 'bg-muted text-muted-foreground',
      primary: 'bg-primary/15 text-primary',
      success: 'bg-success/15 text-success',
      warning: 'bg-warning/15 text-warning',
      info: 'bg-info/15 text-info',
      destructive: 'bg-destructive/15 text-destructive',
      ...CATEGORY_TONE_CLASSES,
    },
    size: {
      sm: 'size-5',
      default: 'size-6',
      lg: 'size-8',
      xl: 'size-9',
      '2xl': 'size-12',
    },
    shape: {
      circle: 'rounded-full',
      square: 'rounded-lg',
    },
    fill: {
      tinted: '',
      solid: '',
    },
  },
  compoundVariants: [
    { fill: 'solid', tone: 'default', className: 'bg-muted-foreground text-background' },
    { fill: 'solid', tone: 'primary', className: 'bg-primary text-primary-foreground' },
    { fill: 'solid', tone: 'success', className: 'bg-success text-success-foreground' },
    { fill: 'solid', tone: 'warning', className: 'bg-warning text-warning-foreground' },
    { fill: 'solid', tone: 'info', className: 'bg-info text-info-foreground' },
    { fill: 'solid', tone: 'destructive', className: 'bg-destructive text-destructive-foreground' },
  ],
  defaultVariants: { tone: 'default', size: 'default', shape: 'circle', fill: 'tinted' },
});

type IconChipProps = React.ComponentProps<'span'> & VariantProps<typeof iconChipVariants>;

// gloss
const IconChip = ({
  className,
  tone = 'default',
  size = 'default',
  shape = 'circle',
  fill = 'tinted',
  ...props
}: IconChipProps) => (
  <span
    data-slot="icon-chip"
    aria-hidden
    className={cn(iconChipVariants({ tone, size, shape, fill, className }))}
    {...props}
  />
);

export type { IconChipProps };
export { IconChip, iconChipVariants };
